/**
 * Mindmap View - Drag and Drop Module
 *
 * Node dragging, side flipping, reordering, reparenting.
 * Uses callback injection pattern for dependencies.
 */

import * as State from './state.js';
import * as MultiSelect from "../../ui/multi-select.js";
import * as Tidy from './tidy-layout.js';

/** Drops closer than this to the minimum clearance snap to it (px). */
const EXTRA_SNAP = 12;

/** Safely escape an ID for use in a CSS attribute selector */
const esc = (id) => CSS.escape(id);

// ============================================================
// Module callbacks (injected via init)
// ============================================================
let dragCallbacks = null;

/**
 * Initialize drag-drop module with callbacks
 * @param {Object} cbs
 * @param {Function} cbs.find - Find node by ID
 * @param {Function} cbs.findParent - Find parent of node
 * @param {Function} cbs.onNodeUpdate - Update node in CRDT
 * @param {Function} cbs.onNodeMove - Move node to new parent
 * @param {Function} cbs.onNodeReorder - Reorder siblings
 * @param {Function} cbs.isReadOnly - Check read-only status
 * @param {Function} cbs.beginBatch - Start CRDT batch
 * @param {Function} cbs.endBatch - End CRDT batch
 * @param {Function} cbs.triggerRender - Trigger mindmap render
 * @param {Function} cbs.updateTransform - Update zoom/pan transform
 */
export function init(cbs) {
  dragCallbacks = cbs;
}

/**
 * Warn once if CRDT batch callbacks are missing
 */
function warnMissingBatchCallbacks() {
  if (!State.warnedMissingCallbacks && (!State.callbacks?.beginBatch || !State.callbacks?.endBatch)) {
    console.warn("[Mindmap] CRDT batch callbacks (beginBatch/endBatch) not provided - batching disabled. Check view wiring in main.js sharedCallbacks.");
    State.setWarnedMissingCallbacks(true);
  }
}

// ============================================================
// Path Update Helpers
// ============================================================

/**
 * Update path between parent and child
 */
export function updatePath(path, parentEl, childEl) {
  const px = parseFloat(parentEl.style.left);
  const py = parseFloat(parentEl.style.top);
  const cx = parseFloat(childEl.style.left);
  const cy = parseFloat(childEl.style.top);
  const ddx = cx - px;
  const cpx1 = px + ddx * 0.4;
  const cpx2 = cx - ddx * 0.4;
  path.setAttribute(
    "d",
    `M${px},${py} C${cpx1},${py} ${cpx2},${cy} ${cx},${cy}`
  );
}

/**
 * Update paths for all descendants
 */
export function updateDescendantPaths(parentNode, parentEl) {
  if (!parentNode.children) return;

  parentNode.children.forEach((child) => {
    const childPath = document.getElementById(`path-${child.id}`);
    const childEl = State.container?.querySelector(`.mm-node[data-id="${esc(child.id)}"]`);
    if (childPath && childEl) {
      updatePath(childPath, parentEl, childEl);
      updateDescendantPaths(child, childEl);
    }
  });
}

/**
 * Check if nodeId is a descendant of potentialAncestorId
 */
export function isDescendant(nodeId, potentialAncestorId) {
  const ancestor = State.callbacks?.find(potentialAncestorId);
  if (!ancestor?.children) return false;

  function check(node) {
    if (node.id === nodeId) return true;
    if (node.children) {
      return node.children.some(check);
    }
    return false;
  }

  return ancestor.children.some(check);
}

/**
 * Update SVG paths for a specific node (to parent and from children)
 */
export function updateNodePaths(nodeId) {
  if (!State.world || !State.svg) return;

  const nodeEl = State.world.querySelector(`.mm-node[data-id="${esc(nodeId)}"]`);
  if (!nodeEl) return;

  const nodeX = parseFloat(nodeEl.style.left) || 0;
  const nodeY = parseFloat(nodeEl.style.top) || 0;

  // Update path to parent
  const path = State.svg.querySelector(`#path-${CSS.escape(nodeId)}`);
  if (path) {
    const node = State.callbacks?.find(nodeId);
    const parent = node ? State.callbacks?.findParent(nodeId) : null;
    if (parent) {
      const parentEl = State.world.querySelector(`.mm-node[data-id="${esc(parent.id)}"]`);
      if (parentEl) {
        const parentX = parseFloat(parentEl.style.left) || 0;
        const parentY = parseFloat(parentEl.style.top) || 0;

        const dx = nodeX - parentX;
        const cp1x = parentX + dx * 0.5;
        const cp1y = parentY;
        const cp2x = parentX + dx * 0.5;
        const cp2y = nodeY;

        path.setAttribute(
          "d",
          `M${parentX},${parentY} C${cp1x},${cp1y} ${cp2x},${cp2y} ${nodeX},${nodeY}`
        );

        // Update gradient
        const gradientId = `gradient-${nodeId}`;
        const gradient = State.svg.querySelector(`#${CSS.escape(gradientId)}`);
        if (gradient) {
          gradient.setAttribute("x1", parentX);
          gradient.setAttribute("y1", parentY);
          gradient.setAttribute("x2", nodeX);
          gradient.setAttribute("y2", nodeY);
        }
      }
    }
  }

  // Update paths from children
  const node = State.callbacks?.find(nodeId);
  if (node && node.children) {
    node.children.forEach((child) => {
      const childPath = State.svg.querySelector(`#path-${CSS.escape(child.id)}`);
      if (childPath) {
        const childEl = State.world.querySelector(`.mm-node[data-id="${esc(child.id)}"]`);
        if (childEl) {
          const childX = parseFloat(childEl.style.left) || 0;
          const childY = parseFloat(childEl.style.top) || 0;

          const dx = childX - nodeX;
          const cp1x = nodeX + dx * 0.5;
          const cp1y = nodeY;
          const cp2x = nodeX + dx * 0.5;
          const cp2y = childY;

          childPath.setAttribute(
            "d",
            `M${nodeX},${nodeY} C${cp1x},${cp1y} ${cp2x},${cp2y} ${childX},${childY}`
          );

          // Update gradient
          const gradientId = `gradient-${child.id}`;
          const gradient = State.svg.querySelector(`#${gradientId}`);
          if (gradient) {
            gradient.setAttribute("x1", nodeX);
            gradient.setAttribute("y1", nodeY);
            gradient.setAttribute("x2", childX);
            gradient.setAttribute("y2", childY);
          }
        }
      }
    });
  }
}

// ============================================================
// Main Drag Setup
// ============================================================

/**
 * Setup drag behavior for a mindmap node
 */
export function setupMmDrag(el, node) {
  let dragging = false;
  let hasMoved = false;
  let startX, startY;
  let downX, downY; // where the press started (startX/Y follow the pointer)
  let dropTarget = null;
  let descendantEls = [];
  let initialDirection = null; // Track original direction at drag start (never changes)
  let lastDirection = null; // Track current direction (updated during drag for mirroring)
  let isRootDrag = false; // Whether we're dragging the focused root (pan mode)

  el.addEventListener("mousedown", (e) => {
    if (e.button !== 0) return;
    // Disable dragging for read-only viewers (only allow pan via root)
    if (
      State.callbacks?.isReadOnly?.() &&
      !el.classList.contains("root")
    ) {
      return;
    }
    e.stopPropagation();
    dragging = true;
    State.setIsDraggingNode(true); // Set global flag to prevent renders
    hasMoved = false;
    el.classList.add("dragging");
    startX = e.clientX;
    startY = e.clientY;
    downX = e.clientX;
    downY = e.clientY;

    // Check if this is the focused root node - if so, drag will pan the canvas
    isRootDrag = el.classList.contains("root");

    // Store the initial direction based on actual position relative to parent
    // (not from dataset.direction which might be outdated if node was dragged)
    const parent = State.callbacks?.findParent(node.id);
    if (parent) {
      const parentEl = State.container?.querySelector(
        `.mm-node[data-id="${esc(parent.id)}"]`
      );
      if (parentEl) {
        const parentX = parseFloat(parentEl.style.left);
        const nodeX = parseFloat(el.style.left);
        initialDirection = nodeX > parentX ? "right" : "left";
      } else {
        initialDirection = el.dataset.direction;
      }
    } else {
      initialDirection = el.dataset.direction;
    }
    lastDirection = initialDirection; // Start tracking from initial

    // Disable transitions on all paths during drag for immediate response
    if (State.svg) {
      State.svg.querySelectorAll(".mm-path").forEach((path) => {
        path.style.transition = "none";
      });
    }

    // Collect all descendant elements for group movement
    descendantEls = [];
    const collectDescendants = (n) => {
      if (n.children) {
        n.children.forEach((child) => {
          const childEl = State.container?.querySelector(
            `.mm-node[data-id="${esc(child.id)}"]`
          );
          if (childEl) {
            descendantEls.push({ el: childEl, node: child });
          }
          collectDescendants(child);
        });
      }
    };
    collectDescendants(el._mmNode || node); // el may be kept across renders

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("keydown", onKey);
    if (!isRootDrag) document.body.classList.add("dragging-node");
  });

  // Esc cancels the drag: nothing is written and the map snaps back.
  function onKey(e) {
    if (e.key !== "Escape" || !dragging) return;
    e.preventDefault();
    e.stopPropagation();
    dropTarget = null;
    finishDrag();
    if (hasMoved) el._justDragged = true;
    if (!isRootDrag) dragCallbacks?.triggerRender?.();
  }

  // Common end of a drag (drop or cancel): listeners, flags, highlights.
  function finishDrag() {
    if (State.svg) {
      State.svg.querySelectorAll(".mm-path").forEach((path) => {
        path.style.transition = "";
      });
    }
    dragging = false;
    State.setIsDraggingNode(false); // Clear global flag
    el.classList.remove("dragging");
    document.removeEventListener("mousemove", onMove);
    document.removeEventListener("mouseup", onUp);
    document.removeEventListener("keydown", onKey);
    document.body.classList.remove("dragging-node");
    State.container?.querySelectorAll(".mm-node.drop-target").forEach((n) => {
      n.classList.remove("drop-target");
    });
  }

  function onMove(e) {
    if (!dragging) return;

    // Distance from the press, not from the last event: a slow drag is a drag.
    const mouseDist = Math.hypot(e.clientX - downX, e.clientY - downY);
    if (mouseDist > 3) {
      hasMoved = true;
    }

    // For root node drag, pan the canvas instead of moving the node
    if (isRootDrag) {
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      State.setMState({
        panX: State.mState.panX + dx,
        panY: State.mState.panY + dy
      });
      startX = e.clientX;
      startY = e.clientY;
      dragCallbacks?.updateTransform?.();
      return;
    }

    const dx = (e.clientX - startX) / State.mState.zoom;
    const dy = (e.clientY - startY) / State.mState.zoom;

    // Store the dragged node's position BEFORE moving it (needed for mirroring)
    const oldNodeX = parseFloat(el.style.left);

    // Move the dragged node
    el.style.left = oldNodeX + dx + "px";
    el.style.top = parseFloat(el.style.top) + dy + "px";

    // Check if we've crossed to the opposite side BEFORE moving descendants
    let shouldMirror = false;
    const parent = State.callbacks?.findParent(node.id);
    if (parent && initialDirection) {
      const parentEl = State.container?.querySelector(
        `.mm-node[data-id="${esc(parent.id)}"]`
      );
      if (parentEl) {
        const parentX = parseFloat(parentEl.style.left);
        const nodeX = parseFloat(el.style.left);
        const newDirection = nodeX > parentX ? "right" : "left";

        // Check if we've crossed to the opposite side from last known direction
        if (newDirection !== lastDirection) {
          shouldMirror = true;
          el.dataset.direction = newDirection;
          el.classList.remove("mm-left", "mm-right");
          el.classList.add(newDirection === "left" ? "mm-left" : "mm-right");

          // Update last direction so we can flip back if dragged to the other side again
          lastDirection = newDirection;
        }
      }
    }

    // Move or mirror descendant nodes based on whether we crossed sides
    if (shouldMirror) {
      // Mirror ALL descendant nodes around THIS node when crossing sides
      // Use the NEW position after moving
      const newNodeX = parseFloat(el.style.left);

      descendantEls.forEach(({ el: descEl }) => {
        // Update direction for descendant
        descEl.dataset.direction = lastDirection;
        descEl.classList.remove("mm-left", "mm-right");
        descEl.classList.add(
          lastDirection === "left" ? "mm-left" : "mm-right"
        );

        // Apply vertical translation
        descEl.style.top = parseFloat(descEl.style.top) + dy + "px";

        // Mirror the x position relative to the OLD position of the dragged node
        // then add the dx movement
        const descX = parseFloat(descEl.style.left);
        const offsetX = descX - oldNodeX;
        descEl.style.left = newNodeX - offsetX + "px";
      });
    } else {
      // Normal movement: translate all descendants by the same delta
      descendantEls.forEach(({ el: descEl }) => {
        descEl.style.left = parseFloat(descEl.style.left) + dx + "px";
        descEl.style.top = parseFloat(descEl.style.top) + dy + "px";
      });
    }

    startX = e.clientX;
    startY = e.clientY;

    // Update SVG paths immediately to stay rigid with nodes
    updateNodePaths(node.id);
    descendantEls.forEach(({ node: descNode }) => {
      updateNodePaths(descNode.id);
    });

    // Check for drop targets (reparenting with Ctrl+drag)
    dropTarget = null;

    if (e.ctrlKey) {
      let closestDist = Infinity;
      let closestNode = null;
      // Dragging a node of the multi-selection moves them all: selected
      // nodes are not targets, the current parent is.
      const bulk = MultiSelect.movesSelection(node.id);
      const currentParentId = bulk ? null : State.callbacks?.findParent(node.id)?.id;

      State.container?.querySelectorAll(".mm-node").forEach((n) => {
        n.classList.remove("drop-target");
        if (n === el) return;
        // Any node outside the dragged subtree can take it, ancestors
        // included (grandparent = one level up); its own parent would be a
        // no-op that only moved it to the end.
        if (n.dataset.id === node.id || n.dataset.id === currentParentId || isDescendant(n.dataset.id, node.id) ||
            (bulk && MultiSelect.has(n.dataset.id)))
          return;

        const rect = n.getBoundingClientRect();
        const dist = Math.hypot(
          e.clientX - rect.left - rect.width / 2,
          e.clientY - rect.top - rect.height / 2
        );

        if (dist < closestDist && dist < 50) {
          closestDist = dist;
          closestNode = n;
        }
      });

      if (closestNode) {
        dropTarget = closestNode;
        closestNode.classList.add("drop-target");
      }
    } else {
      // Clear any drop target highlight when not holding Ctrl
      State.container?.querySelectorAll(".mm-node").forEach((n) => {
        n.classList.remove("drop-target");
      });
    }
  }

  function onUp() {
    if (!dragging) return;
    finishDrag();

    // A click (or a root drag, which pans) changes nothing in the document.
    // Earlier versions saved the node's and all its descendants' positions on
    // every mouseup: frozen offsets that later overlapped, and whole-node
    // writes that could overwrite a collaborator's concurrent edits.
    if (!hasMoved) return;
    el._justDragged = true;
    if (isRootDrag) return;

    if (dropTarget) {
      const targetId = dropTarget.dataset.id;
      if (MultiSelect.movesSelection(node.id)) {
        MultiSelect.moveSelectionTo(targetId);
      } else if (!isDescendant(targetId, node.id) && State.callbacks?.onNodeMove) {
        State.callbacks.onNodeMove(node.id, targetId, -1);
      }
    } else if (!MultiSelect.movesSelection(node.id)) {
      // (A selection dropped nowhere just snaps back.)
      saveDrop();
    }
    // Snap everything back into the computed layout.
    dragCallbacks?.triggerRender?.();
  }

  /**
   * Persist what the user expressed by dropping the node: its order among its
   * siblings (from Y), its side for the displayed root's children, and extra
   * horizontal distance from its parent. Positions themselves are never
   * stored — the layout recomputes them (see tidy-layout.js).
   */
  function saveDrop() {
    // The Store may have been rebuilt since this element was rendered.
    const current = State.callbacks?.find(node.id) || node;
    const parent = State.callbacks?.findParent(node.id);
    const parentEl = parent && State.container?.querySelector(`.mm-node[data-id="${esc(parent.id)}"]`);
    if (!parentEl || !parent.children) return;

    const level0 = parentEl.classList.contains("root");
    const balanced = (State.layoutMode || "balanced") === "balanced";
    const parentX = parseFloat(parentEl.style.left);
    const x = parseFloat(el.style.left);
    const y = parseFloat(el.style.top);
    const droppedSide = x >= parentX ? "right" : "left";
    // Only the displayed root's children can change side; deeper nodes follow
    // their branch, and forced layout modes put every branch on one side.
    const side = level0 && balanced ? droppedSide : initialDirection || droppedSide;

    const children = parent.children;
    const rendered = (child) => State.container?.querySelector(`.mm-node[data-id="${esc(child.id)}"]`);

    // New order: among the visible siblings on the drop side, by Y.
    const sameSide = children.filter((c) => {
      if (c.id === node.id) return false;
      const cEl = rendered(c);
      return cEl && (cEl.dataset.direction || side) === side;
    });
    const newIndex = sameSide.filter((c) => parseFloat(rendered(c).style.top) < y).length;
    let reorder = null;
    let order = children;
    if (sameSide.length) {
      reorder = newIndex === 0
        ? { targetId: sameSide[0].id, position: "above" }
        : { targetId: sameSide[newIndex - 1].id, position: "below" };
      order = children.filter((c) => c.id !== node.id);
      const at = order.findIndex((c) => c.id === reorder.targetId) + (reorder.position === "below" ? 1 : 0);
      order.splice(at, 0, current);
      if (order.every((c, i) => c.id === children[i].id)) reorder = null;
    }

    const writes = new Map(); // nodeId -> updates
    if (level0 && balanced) {
      // Pin the dropped side when the default rule wouldn't give it, and pin
      // every other branch the reorder would otherwise move across.
      const withoutPin = Tidy.assignSides(order, "balanced", (c) => (c.id === node.id ? undefined : c.mmSide));
      if (current.mmSide !== side && (current.mmSide || withoutPin.get(node.id) !== side)) {
        writes.set(node.id, { mmSide: side });
      }
      const before = Tidy.assignSides(children);
      const after = Tidy.assignSides(order, "balanced", (c) => (c.id === node.id ? side : c.mmSide));
      for (const c of children) {
        if (c.id !== node.id && before.get(c.id) !== after.get(c.id)) {
          writes.set(c.id, { mmSide: before.get(c.id) });
        }
      }
    }

    // Horizontal distance beyond the minimum clearance, if dropped on its side.
    if (droppedSide === side) {
      const own = el._mmLayoutSize || { w: el.offsetWidth };
      const parentSize = parentEl._mmLayoutSize || { w: parentEl.offsetWidth };
      const gap = Math.abs(x - parentX) - (parentSize.w / 2 + Tidy.H_GAP + own.w / 2);
      const extra = gap < EXTRA_SNAP ? 0 : Math.round(gap);
      const level = level0 ? 0 : 1;
      if (Math.abs(extra - Tidy.extraFromOffset(current.mmOffsetX, level)) > 2) {
        writes.set(node.id, { ...writes.get(node.id), mmOffsetX: Tidy.offsetForExtra(extra, level, side) });
      }
    }

    if (!writes.size && !reorder) return;
    warnMissingBatchCallbacks();
    State.callbacks?.beginBatch?.();
    try {
      for (const [id, updates] of writes) State.callbacks?.onNodeUpdate?.(id, updates);
      if (reorder) State.callbacks?.onNodeReorder?.(node.id, reorder.targetId, reorder.position);
    } finally {
      State.callbacks?.endBatch?.();
    }
  }
}
